// Formatting helpers for the revenue feature. Revenue dates are ISO YYYY-MM-DD
// end to end (the API's wire format — the one deliberate exception to the
// DD/MM/YYYY document convention), so everything here parses ISO only.

export const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Deliberately hand-rolled: Intl month names vary by ICU build, and these
// short forms match the app's own Hebrew register.
const MONTHS_SHORT = ['ינו׳', 'פבר׳', 'מרץ', 'אפר׳', 'מאי', 'יוני', 'יולי', 'אוג׳', 'ספט׳', 'אוק׳', 'נוב׳', 'דצמ׳'];

export function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Inclusive day count between two ISO dates; 0 when the range is invalid. */
export function isoDaysBetween(from, to) {
  if (!ISO_DATE_RE.test(from || '') || !ISO_DATE_RE.test(to || '') || from > to) return 0;
  const a = new Date(`${from}T00:00:00`);
  const b = new Date(`${to}T00:00:00`);
  return Math.round((b - a) / 86400000) + 1;
}

export function addIsoDays(iso, n) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** '2026-08-19' → '19 באוג׳' (with year when it differs from the reference). */
export function formatIsoDay(iso, { withYear = false } = {}) {
  if (!ISO_DATE_RE.test(iso || '')) return iso || '';
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ב${MONTHS_SHORT[m - 1]}${withYear ? ` ${y}` : ''}`;
}

/**
 * '2026-08-19'..'2026-08-26' → '19–26 באוג׳'; cross-month ranges name both
 * days in full. A single-day range is just the day.
 */
export function formatIsoRange(from, to) {
  if (from === to) return formatIsoDay(from);
  const sameMonth = from?.slice(0, 7) === to?.slice(0, 7);
  if (sameMonth) {
    const [, m, dTo] = to.split('-').map(Number);
    const dFrom = Number(from.split('-')[2]);
    return `${dFrom}–${dTo} ב${MONTHS_SHORT[m - 1]}`;
  }
  return `${formatIsoDay(from)} – ${formatIsoDay(to)}`;
}

/** The banner's "חסרים: …" fragment — first ranges, compressed by the server. */
export function missingRangesLabel(ranges = [], maxShown = 2) {
  const shown = ranges.slice(0, maxShown).map(r => formatIsoRange(r.from, r.to));
  const extra = ranges.length - shown.length;
  return shown.join(', ') + (extra > 0 ? ` ועוד ${extra}` : '');
}

/** '2026-08' → 'אוג׳ 2026' (the coverage view's month header). */
export function formatIsoMonth(month) {
  if (!/^\d{4}-\d{2}$/.test(month || '')) return month || '';
  const [y, m] = month.split('-').map(Number);
  return `${MONTHS_SHORT[m - 1]} ${y}`;
}

export function addMonths(month, n) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function currentMonth() {
  return isoToday().slice(0, 7);
}

/**
 * The "*הוזנו הכנסות, עבור …" line — shared by the /analysis card and the
 * הכנסות מול הוצאות report so the two cannot drift. Returns
 * {before, range, after, stale} or null when the line should not show.
 *
 * `entry_frontier` is the ONE field on the payload that is not scoped to the
 * picked range (see computeEntryFrontier) — it always describes today. That is
 * why it is SUPPRESSED on a historical range: reviewing August in October, a
 * line about where September's entry stands reads as a bug. The amber missing-
 * days notice still reports August's own completeness.
 *
 * `today` is a parameter so callers pass their own isoToday() — a module-level
 * mock of isoToday cannot reach a call made from inside this module.
 */
export function entryFrontierLine({ frontier: f, hasRevenue, dateTo, bannerShowing, today = isoToday() }) {
  // No revenue ever typed: the empty state already says so, louder and better.
  if (!f || !hasRevenue) return null;
  const yesterday = addIsoDays(today, -1);
  if (dateTo < yesterday) return null;
  if (!f.entered_through) {
    // The only case where this line really is a second WARNING rather than
    // provenance — so when the amber notice is already saying days are
    // missing, it stands down and lets the louder one speak.
    if (bannerShowing) return null;
    return { before: '*לא הוזנו הכנסות בחודשיים האחרונים', range: null, after: '', stale: true };
  }
  const stale = f.days_behind >= 2;
  return {
    // Verb first: "עבור X הוזנו הכנסות" read as broken Hebrew (owner).
    before: '*הוזנו הכנסות, עבור ',
    range: formatIsoRange(f.entered_from, f.entered_through),
    // The lag only from TWO days — "לפני יום" is noise. Singular יום is the
    // correct count word above ten in Hebrew.
    after: stale ? `, לפני ${f.days_behind} יום` : '',
    stale,
  };
}
